import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { API_URL } from '@captaincause/lib/api/url'
import { useMutation } from '@captaincause/lib/api/useMutation'
import { mustBeEmailAddress } from '@captaincause/lib/form/mustBeEmailAddress'
import { mustBeRequired } from '@captaincause/lib/form/mustBeRequired'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { Submit } from '@captaincause/ui/Button/Submit'
import { CheckboxField } from '@captaincause/ui/Checkbox/Field'
import { Form } from '@captaincause/ui/Form'
import { TextInputField } from '@captaincause/ui/TextInput/Field'
import { tm } from '@captaincause/ui/tm'
import { Button } from 'components/Button'
import type { Dift, Locales, Project } from '@captaincause/types'

interface Values {
  comment?: string
  email?: string
  cgu: boolean
  code?: string
}

interface DonateTriggerArgs extends Values {
  project_id: string
  locale?: string
}

const initialValues: Values = {
  comment: '',
  email: '',
  cgu: false,
  code: undefined,
}

interface DonateFormProps {
  dift: Dift
  project: Project
  locale: Locales
}

export function DonateForm({ dift, project, locale }: DonateFormProps) {
  const { t } = useTranslation(['give.page', 'common'])
  const router = useRouter()

  const { track } = useMixpanel()

  const { trigger: triggerDonateDift } = useMutation<Dift, DonateTriggerArgs>({
    url: `${API_URL}/give/difts/${dift.id}/redeem`,
  })

  const mixpanelPayload = {
    dift_id: dift.id,
    dift_amount: dift.amount.amount,
    project_id: project.id,
    project_name: project.name,
    project_organization_name: project.organization.name,
  }

  const submit = async (values: Values) => {
    track('Submit donate', { ...mixpanelPayload })

    const result = await triggerDonateDift({
      project_id: project.id,
      email: values.email,
      cgu: values.cgu,
    })

    return result
  }

  const submitSuccess = async (result: Dift | undefined) => {
    if (result?.id) {
      track('Submit donate success', {
        donate_id: result.id,
        ...mixpanelPayload,
      })
      await router.push(`/give/thankyou?dift_id=${result.id}`)
    }
  }

  return (
    <Form<Values, Dift>
      onSubmit={submit}
      onSubmitSuccess={submitSuccess}
      initialValues={initialValues}
      submitDelay={1000}
      locale={locale}
      withToastOnFailure
      className={tm('flex h-full flex-col justify-between gap-6 px-4')}
    >
      <>
        <TextInputField
          id="email"
          name="email"
          placeholder={t('donate.form.email.placeholder')}
          labelClassName="mt-4 text-sm text-dark-gray text-left"
          label={t('donate.form.email.label')}
          className="text-base"
          validators={{
            mustBeRequired,
            mustBeEmailAddress,
          }}
          locale={locale}
          autoComplete="email"
        />
        <div className="flex flex-col gap-4">
          <CheckboxField
            name="cgu"
            size="small"
            validators={{ mustBeRequired }}
            locale={locale}
            labelClassName="text-sm text-dark-gray mt-2"
          >
            <Trans
              t={t}
              i18nKey="donate.form.cgu.label"
              values={{ cgu: t('common:cgu') }}
              components={{
                a: (
                  <Button
                    variant="tertiary"
                    className="inline-block text-sm text-dark-gray"
                    href="https://dift.com/cgu"
                  />
                ),
              }}
            />
          </CheckboxField>

          <div className="mb-10 flex w-full justify-center md:mb-0">
            <Submit variant="primary" className="w-full">
              {t('donate.form.submit')}
            </Submit>
          </div>
        </div>
      </>
    </Form>
  )
}
